import React, { useState } from 'react';
import DualRangeSlider from '../../../components/onboarding/DualRangeSlider';
import { SECTORS, DEFAULT_COMFORT_ZONES } from '../../../utils/sectors';

export default function ComfortZoneStep({ onNext, onBack, initialValues }) {
  const [zones, setZones] = useState(initialValues || DEFAULT_COMFORT_ZONES);

  // Priority order — member drags/reorders, or we use a simpler approach: ranking buttons
  const [priority, setPriority] = useState(
    initialValues?.priority_sectors?.length
      ? initialValues.priority_sectors
      : SECTORS.map(s => s.key)
  );

  const handleZoneChange = (sectorKey, { min, max }) => {
    setZones(prev => ({
      ...prev,
      [`${sectorKey}_comfort_min`]: min,
      [`${sectorKey}_comfort_max`]: max,
    }));
  };

  const movePriority = (index, direction) => {
    const newOrder = [...priority];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setPriority(newOrder);
  };

  const handleNext = () => {
    onNext({ ...zones, priority_sectors: priority });
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <h2 className="text-xl font-bold text-reset-brown mb-1">Your comfort zones</h2>
      <p className="text-sm text-gray-500 mb-6 leading-relaxed">
        For each sector, drag the slider to set your comfortable range on a 1–10 scale.
        <span className="block mt-1 text-gray-400">1 = struggling, 10 = thriving. Pick the range where you feel like yourself.</span>
      </p>

      <div className="space-y-7">
        {SECTORS.map(sector => {
          const min = zones[`${sector.key}_comfort_min`];
          const max = zones[`${sector.key}_comfort_max`];

          return (
            <div key={sector.key} className={`rounded-2xl p-4 ${sector.bgClass}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">{sector.emoji}</span>
                <div>
                  <h3 className={`font-semibold text-sm ${sector.textClass}`}>{sector.label}</h3>
                  <p className="text-xs text-gray-500">{sector.description}</p>
                </div>
              </div>
              <DualRangeSlider
                min={min}
                max={max}
                color={sector.color}
                label={`${sector.label} comfort zone`}
                onChange={(vals) => handleZoneChange(sector.key, vals)}
              />
            </div>
          );
        })}
      </div>

      {/* Priority ranking */}
      <div className="mt-8">
        <h3 className="font-semibold text-reset-brown mb-1 text-sm">Sector priority order</h3>
        <p className="text-xs text-gray-500 mb-3">
          Which sector matters most to you right now? The system uses this to personalise your nudges.
          Drag or use the arrows to reorder.
        </p>
        <div className="space-y-2">
          {priority.map((key, index) => {
            const sector = SECTORS.find(s => s.key === key);
            return (
              <div
                key={key}
                className={`flex items-center gap-3 rounded-xl px-3 py-2 ${sector.bgClass} border border-transparent`}
              >
                <span className="text-xs font-bold text-gray-400 w-5 text-center">{index + 1}</span>
                <span className="text-base">{sector.emoji}</span>
                <span className={`text-sm font-medium flex-1 ${sector.textClass}`}>{sector.label}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => movePriority(index, -1)}
                    disabled={index === 0}
                    className="w-6 h-6 rounded-full bg-white text-gray-400 text-xs disabled:opacity-30 hover:text-gray-700 flex items-center justify-center"
                    aria-label="Move up"
                  >↑</button>
                  <button
                    onClick={() => movePriority(index, 1)}
                    disabled={index === priority.length - 1}
                    className="w-6 h-6 rounded-full bg-white text-gray-400 text-xs disabled:opacity-30 hover:text-gray-700 flex items-center justify-center"
                    aria-label="Move down"
                  >↓</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3 mt-8">
        <button
          onClick={onBack}
          className="flex-1 border border-gray-300 text-gray-600 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
        >
          ← Back
        </button>
        <button
          onClick={handleNext}
          className="flex-[2] bg-reset-terracotta text-white py-3 rounded-xl font-semibold text-sm hover:opacity-90 transition"
        >
          Next: My activities →
        </button>
      </div>
    </div>
  );
}
